/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {
  ThirdPartyAudienceApiService,
  ThirdPartyAudienceModel
} from './third-party-audience-api.service';
import {ThirdPartyAudienceEditPageComponent} from './third-party-audience-edit-page.component';
import {ThirdPartyAudienceRoutePageComponent} from './third-party-audience-route-page.component';

export function thirdPartyAudienceStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(ThirdPartyAudienceApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const rh = adnRouterHelper.createRouterHelper(
    'third-party-audiences.third-party-audience',
    ThirdPartyAudienceApiService,
    'app.adv',
    'app'
  );
  const listReadOnly = () => !permissions.hasNetwork('MANAGE_SYSTEM')
    && !permissions.hasAny('MANAGE_AUDIENCES');
  const itemReadOnly = (model: ThirdPartyAudienceModel, isNew: boolean): boolean => {
    if (permissions.hasNetworkPermission('MANAGE_SYSTEM')) {
      return false;
    }
    if (isNew) {
      return !permissions.hasAny('MANAGE_AUDIENCES');
    }
    const teams = model.teams || [];
    return !teams.length || teams.some((team: any) => {
      const teamId = typeof team === 'object' ? team.id : team;
      return !permissions.has('MANAGE_AUDIENCES', teamId);
    });
  };

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly},
    views: {
      'content@app': {component: ThirdPartyAudienceRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {
        component: ListDefaultActionsComponent,
        bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}
      },
      'title@app': stateTitleView()
    }
  }));

  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      thirdPartyAudiencePage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: ThirdPartyAudienceModel = {
            id: $stateParams.id,
            objectState: 'ACTIVE',
            validationWarnings: []
          };
          return new SignalAdminResourcePageState(
            model,
            true,
            itemReadOnly(model, true),
            api,
            'Third-Party Audience'
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model,
          false,
          itemReadOnly(model, false),
          api,
          'Third-Party Audience'
        ));
      },
      model: function(thirdPartyAudiencePage: SignalAdminResourcePageState<ThirdPartyAudienceModel>) {
        return thirdPartyAudiencePage.model();
      }
    },
    views: {
      'content@app': {component: ThirdPartyAudienceEditPageComponent, bindings: {state: 'thirdPartyAudiencePage'}},
      'context@app': {
        component: SignalAdminResourceContextActionsComponent,
        bindings: {state: 'thirdPartyAudiencePage'}
      },
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'thirdPartyAudiencePage'}}
    }
  }));

  return states.build();
}
